import React, { useEffect, useRef } from 'react';
import { useRelay } from '../../../hooks/useRelay';
import { applyAction } from '../../../games/kill-team-draft/kill-team-draft';
import type { KTDState, KTDAction } from '../../../games/kill-team-draft/kill-team-draft';
import type { RoomView } from '../../../types/room';
import PackSelection from './components/PackSelection';

interface Props {
  slug: string;
  room: RoomView;
  myId: string;
  isLeader: boolean;
  initialState: KTDState | null;
  onFinish: () => void;
}

export default function KillTeamDraftRoomGame({ slug, room: _room, myId, isLeader, initialState, onFinish: _onFinish }: Props) {
  const { state, startRelay, sendAction } = useRelay<KTDState>(slug);
  const relayStartedRef = useRef(false);

  useEffect(() => {
    if (!isLeader || !initialState || relayStartedRef.current) return;
    relayStartedRef.current = true;
    startRelay('kill-team-draft', initialState);
  }, [isLeader, initialState, startRelay]);

  if (!state) {
    return <div className="text-white p-10">Loading Kill Team Draft...</div>;
  }

  const dispatch = (action: KTDAction) => {
    sendAction(action, applyAction(state, action));
  };

  const myPack = state.queues[myId]?.[0] ?? [];
  const myDeck = state.decks[myId] ?? [];
  const packsWaiting = Math.max(0, (state.queues[myId]?.length ?? 1) - 1);

  const otherPlayers = state.players.filter(p => p.id !== myId);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Kill Team Draft</h1>
        <div className="flex items-center gap-6 text-sm text-gray-300">
          <span>Pack <strong className="text-white">{state.packNumber}</strong> of {state.config.totalPacks}</span>
          <span>Passing: <strong className="text-white capitalize">{state.passingDirection}</strong></span>
          <span>Phase: <strong className="text-white capitalize">{state.phase}</strong></span>
        </div>
      </div>

      {/* Other players */}
      {otherPlayers.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-3">
          {otherPlayers.map(p => {
            const theirDeck = state.decks[p.id] ?? [];
            const theirQueue = state.queues[p.id] ?? [];
            return (
              <div key={p.id} className="bg-gray-800 px-4 py-2 rounded-lg text-sm">
                <span className="font-semibold">{p.name}</span>
                <span className="text-gray-400 ml-2">Deck: {theirDeck.length}</span>
                {theirQueue.length > 0 && (
                  <span className="text-yellow-400 ml-2">Queue: {theirQueue.length}</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Draft complete */}
      {state.phase === 'finished' && (
        <div className="mb-6 bg-green-900/50 border border-green-600 p-4 rounded-lg text-center">
          <div className="text-xl font-bold">Draft Complete!</div>
          <div className="text-sm text-gray-300 mt-1">
            {isLeader && 'As the host, you can start a new draft.'}
          </div>
        </div>
      )}

      {/* Waiting for a pack */}
      {state.phase === 'drafting' && myPack.length === 0 && (
        <div className="mb-6 bg-blue-900/30 border border-blue-600/50 p-6 rounded-lg text-center">
          <div className="text-xl text-blue-400 font-semibold">Waiting for a pack...</div>
          <div className="text-sm text-blue-200 mt-2">Someone is still picking from the pack heading your way.</div>
        </div>
      )}

      {/* Queued packs notice */}
      {packsWaiting > 0 && (
        <div className="mb-4 bg-yellow-900/30 border border-yellow-600/50 p-3 rounded-lg text-sm">
          <span className="text-yellow-400 font-semibold">
            {packsWaiting} pack{packsWaiting > 1 ? 's' : ''} waiting in queue
          </span>
          <span className="text-yellow-200 ml-2">— pick from your current pack to continue.</span>
        </div>
      )}

      {/* Current pack */}
      {myPack.length > 0 && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4">
            Current Pack ({myPack.length} cards)
            {packsWaiting > 0 && (
              <span className="text-lg text-yellow-400 ml-2">+{packsWaiting} waiting</span>
            )}
          </h2>
          <PackSelection
            cards={myPack}
            onSelectCard={(cardId) => dispatch({ type: 'selectCard', playerId: myId, cardId })}
          />
        </div>
      )}

      {/* Drafted deck */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4">My Deck ({myDeck.length} cards)</h2>
        {myDeck.length === 0 ? (
          <div className="text-gray-500 text-center py-8">No cards drafted yet.</div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-8 lg:grid-cols-12 gap-2">
            {myDeck.map(card => (
              <div
                key={card.id}
                className="bg-gray-700 rounded px-2 py-1 text-center text-sm font-semibold"
              >
                {card.name}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Leader controls */}
      {state.phase === 'finished' && isLeader && (
        <div className="text-center">
          <button
            onClick={() => dispatch({ type: 'resetDraft', playerId: myId })}
            className="bg-green-600 hover:bg-green-700 px-6 py-3 rounded-lg text-lg font-semibold transition"
          >
            Draft Again
          </button>
        </div>
      )}
    </div>
  );
}
